import React, { useState } from "react";
import { toast } from "react-toastify";
import { FileSignature, X, ExternalLink, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import ESignStatusBadge from "../../components/ESignStatusBadge";
import SSPPassphraseModal from "../../components/SSPPassphraseModal";
import { useUser } from "../../context/UserContext";
import {
  previewInteriorAgreement,
  sendInteriorAgreement,
  getSignatureRequests,
  requestSigningUrl,
} from "../../services/esign";

const DOC_TYPE = "INTERIOR_EXECUTION_AGREEMENT";
const TERMINAL = new Set(["COMPLETED", "EXPIRED", "DECLINED", "CANCELLED", "FAILED"]);
const SIGNED_SIGNER = new Set(["SIGNED", "DECLINED", "EXPIRED"]);

// Which agreement signer role the logged-in staff persona may sign (buyers sign in the mobile app).
const personaSignRole = (persona) => {
  if (persona === "ZUMALE_USER") return "BUILDVIZER";
  if (persona === "DEVELOPER_ADMIN" || persona === "DEVELOPER") return "DEVELOPER";
  return null;
};

/**
 * Interior Design & Execution Agreement — staff panel for one lead. Generate → preview → send the
 * N-party agreement (buyers eSign in the mobile app; the Developer and BuildVizer representatives
 * sign here on the web). Enabled once at least one package cost estimate (BOQ) has been shared
 * with the buyer.
 */
const InteriorAgreementPanel = ({ leadId, boqReady }) => {
  const { userInfo } = useUser();
  const signRole = personaSignRole(userInfo?.persona);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { customerSalesId, documentHtml, parties }
  const [requests, setRequests] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [reps, setReps] = useState({
    developerRepName: "", developerRepEmail: "", developerRepPhone: "",
    buildvizerRepName: "", buildvizerRepEmail: "", buildvizerRepPhone: "",
  });
  const [passOpen, setPassOpen] = useState(false);

  const liveRequest = requests.find((r) => !TERMINAL.has(r.status)) || requests[0] || null;

  const refreshRequests = async (customerSalesId) => {
    if (!customerSalesId) return;
    try {
      setRequests(await getSignatureRequests(customerSalesId, DOC_TYPE));
    } catch (_e) { /* status is best-effort */ }
  };

  const openPanel = async () => {
    setLoading(true);
    try {
      const p = await previewInteriorAgreement({ idLeadId: leadId });
      setPreview(p);
      await refreshRequests(p.customerSalesId);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare the interior agreement");
    } finally {
      setLoading(false);
    }
  };

  const doSend = async (passphrase) => {
    // Thrown errors propagate to SSPPassphraseModal, which handles SSL/passphrase messages.
    const result = await sendInteriorAgreement({ idLeadId: leadId, passphrase, ...reps });
    setPassOpen(false);
    toast.success("Interior agreement generated and sent for signing");
    await refreshRequests(result.customerSalesId);
  };

  const signAs = async (requestId, role) => {
    try {
      const { url } = await requestSigningUrl(requestId, role);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the signing page");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mt-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSignature size={18} className="text-indigo-600" />
          <div>
            <p className="text-sm font-semibold text-slate-800">Interior Design &amp; Execution Agreement</p>
            <p className="text-xs text-slate-500">
              {preview?.isBvManaged
                ? "BuildVizer and Buyer(s) sign via Aadhaar eSign."
                : "Developer, BuildVizer and Buyer(s) sign via Aadhaar eSign."}
            </p>
          </div>
        </div>
        <button
          onClick={openPanel}
          disabled={!boqReady || loading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
            hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          title={boqReady ? "" : "Share a package cost estimate (BOQ) with the buyer first"}
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <FileSignature size={15} />}
          Manage agreement
        </button>
      </div>
      {!boqReady && (
        <p className="mt-2 text-xs text-amber-600">Share at least one package cost estimate (BOQ) with the buyer before generating the agreement.</p>
      )}

      {open && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Interior Agreement</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4">
              {/* Parties */}
              <div className="mb-4">
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Parties</p>
                <ul className="space-y-1">
                  {preview.parties.map((p, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">
                        {p.orgName || p.individualName || "—"}
                        <span className="ml-1 text-xs text-slate-400">· {p.role.replace(/_/g, " ").toLowerCase()}</span>
                      </span>
                      {p.isSigner && <span className="text-xs text-indigo-600">signs</span>}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Signing status */}
              {liveRequest ? (
                <div className="mb-4 rounded-xl border border-slate-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signing status</span>
                    <ESignStatusBadge status={liveRequest.status} />
                  </div>
                  <ul className="space-y-1.5">
                    {liveRequest.signers.map((s) => {
                      const canSign =
                        signRole && s.role === signRole && !SIGNED_SIGNER.has(s.status) && !TERMINAL.has(liveRequest.status);
                      return (
                        <li key={s.esignSignerId} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">
                            {s.name || "—"}
                            <span className="ml-1 text-xs text-slate-400">· {s.role.replace(/_/g, " ").toLowerCase()}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{s.status.toLowerCase()}</span>
                            {canSign && (
                              <button
                                onClick={() => signAs(liveRequest.esignSignatureRequestId, s.role)}
                                className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                              >
                                <ExternalLink size={12} /> Sign
                              </button>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {liveRequest.status === "COMPLETED" && liveRequest.signedPdfUrl && (
                    <a href={liveRequest.signedPdfUrl} target="_blank" rel="noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline">
                      <ExternalLink size={12} /> View signed agreement
                    </a>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Authorized representatives (optional — defaults to each party's registered contact)
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {!preview.isBvManaged && (
                      <>
                        <input className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="Developer rep name"
                          value={reps.developerRepName} onChange={(e) => setReps({ ...reps, developerRepName: e.target.value })} />
                        <input className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="Developer rep email"
                          value={reps.developerRepEmail} onChange={(e) => setReps({ ...reps, developerRepEmail: e.target.value })} />
                      </>
                    )}
                    <input className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="BuildVizer rep name"
                      value={reps.buildvizerRepName} onChange={(e) => setReps({ ...reps, buildvizerRepName: e.target.value })} />
                    <input className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="BuildVizer rep email"
                      value={reps.buildvizerRepEmail} onChange={(e) => setReps({ ...reps, buildvizerRepEmail: e.target.value })} />
                  </div>
                </div>
              )}

              {/* Preview */}
              <button onClick={() => setShowPreview((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800">
                {showPreview ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Preview document
              </button>
              {showPreview && (
                <iframe title="Agreement preview" srcDoc={preview.documentHtml}
                  className="mt-2 h-80 w-full rounded-lg border border-slate-200" />
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">
                Close
              </button>
              {!liveRequest && (
                <button onClick={() => setPassOpen(true)}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700">
                  Generate &amp; Send for signing
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SSPPassphraseModal isOpen={passOpen} onClose={() => setPassOpen(false)} onConfirm={doSend} />
    </div>
  );
};

export default InteriorAgreementPanel;
